import { html, render } from 'lit-html'
import { unsafeHTML } from 'lit-html/directives/unsafe-html'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import './style.scss'

const VALID_TYPES = [
  'svg',
  'glyph',
  'text',
  'legacy-svg',
  'legacy-glyph'
]

export async function importSvgIcon (type, name) {
  if (type.includes('legacy')) {
    return (await import(
      /* webpackChunkName: 'legacy-[request]' */
      `@polleverywhere/assets/icons/${name}.svg`
    )).default
  } else {
    return (await import(
      /* webpackChunkName: '[request]' */
      `@polleverywhere/icons/src/svgs/${name}.svg`
    )).default
  }
}

async function svgTemplate (type, name) {
  const svgIcon = await importSvgIcon(type, name)
  return svgIcon ? html`${unsafeHTML(svgIcon)}` : ''
}

function glyphTemplate (type, name) {
  return type.includes('legacy')
    ? html`
    <span class=${bem('legacy-glyph')}
        data-glyph='${name}'
        aria-hidden='true'></span>`
    : html`
    <span class=${bem('glyph')}
        data-glyph-name='${name}'
        aria-hidden='true'></span>`
}

function textTemplate (type, name) {
  // Uppercase first two letters of icon text to render into an icon.
  const initials = name.substring(0, 2).toUpperCase()
  return html`
  <div class=${bem('text')} name='${initials}' aria-hidden='true'>
    <span class=${bem('text-label')}>${initials}</span>
  </div>`
}

async function template (type, name) {
  if (!VALID_TYPES.includes(type)) return ''

  const svgTpl = type.includes('svg') ? await svgTemplate(type, name) : ''
  const glyphTpl = type.includes('glyph') ? glyphTemplate(type, name) : ''
  const textTpl = type.includes('text') ? textTemplate(type, name) : ''

  return html`
    ${svgTpl}
    ${glyphTpl}
    ${textTpl}
  `
}

function renderTemplate () {
  if (!this.isConnected) return
  const type = this.getAttribute('type') || undefined
  const name = this.getAttribute('name') || ''

  template(type, name).then((templateResult) => {
    render(templateResult, this)
  }).catch((e) => console.error(e))
}

/**
 * Icon element lets consumers easily render either an SVG vector icon, font
 * glyph or a two-letter, text-based icon. Consumers must provide both the
 * `type` and `name` of the icon to render. Font glyphs and SVGs are rendered
 * either from the legacy `@pe/assets` repo or `@pe/icons` for v2 icons.
 * Text-based icons uses the first two characters in `name` as the icon.
 *
 * For examples:
 * `<icon-element type='svg' name='my-svg'></icon-element>`
 * `<icon-element type='glyph' name='gear'></icon-element>`
 * `<icon-element type='legacy-svg' name='ok-green'></icon-element>`
 * `<icon-element type='legacy-glyph' name='anonymous'></icon-element>`
 * `<icon-element type='text' name='da'></icon-element>`
 *
 * Scale the icon up or down using the `font-size` property.
 * ```
 * <icon-element style='font-size: 2rem' type='glyph' name='gear'></icon-element>
 * ```
 *
 * @element icon-element
 *
 * @attr {string} type - Type of icon to render. Legacy types are loaded from
 *    `@pe/assets`. Otherwise, uses `@pe/icons`. Valid types are:
 *    `svg, glyph, text, legacy-svg, legacy-glyph`
 * @attr {string} name - Name of the SVG or font glyph. For text-based icons,
 *    first two characters are used.
 *
 * @cssprop --icon-element-color - Apply primary color to all icon types. Not
 *  all raw SVG icons support color changes this way and requires special syntax
 *  to be included in their markup.
 * @cssprop --icon-element-secondary-color - Apply a secondary color to icons
 *  that support multiple colors, specifically to SVG or text icons only. This
 *  combined with primary color can create duotone icons.
 * @cssprop --icon-element-transition - Apply a transition rule to the different
 *  parts of the icon depending on its type. Typically used to transition icon
 *  color switches by defining transition properties for border, color and/or
 *  fill, depending on the type.
 */
export default class IconElement extends HTMLElement {
  static get tagName () { return 'icon-element' }

  static get observedAttributes () {
    return ['type', 'name']
  }

  connectedCallback () {
    renderTemplate.call(this)
  }

  attributeChangedCallback () {
    renderTemplate.call(this)
  }
}

const bem = bemBlock(IconElement.tagName)
register(module, IconElement.tagName, IconElement)
